import { queryOptions } from '@tanstack/react-query'
import type {
  GetDailyScansResult,
  GetHourlyScansResult,
  GetOrganizationScanTotalsResult,
  GetScanPlacesResult,
  GetScanSummaryResult,
} from 'constellation-sdk'
import { apiFetch } from '@/lib/api/fetch'
import { keys } from '@/lib/queries/keys'
import type { HourPoint, Place, ScanSummary } from '@/lib/types'

/**
 * Scan analytics.
 *
 * These come from the REST aggregates rather than pg_graphql, which cannot
 * express a GROUP BY. Every figure excludes test scans and is bucketed in the
 * viewer's own time zone — "scans today" has to mean the customer's today.
 */

/**
 * The browser's zone, resolved once.
 *
 * Part of the query key, so switching zones (or a colleague in another one
 * opening the same screen) gets its own cache entry rather than being handed
 * numbers bucketed against someone else's midnight.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/**
 * Analytics are cheap to be slightly stale and expensive to refetch on every
 * mount — a scan count that lags a minute costs nothing, whereas five
 * aggregates per navigation is real database work.
 */
const ANALYTICS_STALE_TIME = 2 * 60_000

export function scanSummaryQuery(
  organizationId: string,
  constellationId: string,
  timeZone = browserTimeZone(),
) {
  return queryOptions({
    queryKey: keys.analyticsSummary(organizationId, constellationId, timeZone),
    queryFn: ({ signal }) =>
      apiFetch<GetScanSummaryResult>(
        `/constellations/${constellationId}/analytics/summary?tz=${encodeURIComponent(timeZone)}`,
        { signal },
      ),
    staleTime: ANALYTICS_STALE_TIME,
    select: (data): ScanSummary =>
      data.summary ?? {
        today: 0,
        yesterday: 0,
        last7Days: 0,
        last30Days: 0,
        lifetime: 0,
        lastScanAt: null,
      },
  })
}

export function hourlyScansQuery(
  organizationId: string,
  constellationId: string,
  timeZone = browserTimeZone(),
) {
  return queryOptions({
    queryKey: keys.analyticsHourly(organizationId, constellationId, timeZone),
    queryFn: ({ signal }) =>
      apiFetch<GetHourlyScansResult>(
        `/constellations/${constellationId}/analytics/hourly?tz=${encodeURIComponent(timeZone)}`,
        { signal },
      ),
    staleTime: ANALYTICS_STALE_TIME,
    /* The API always returns all 24 buckets, so an empty array here means the
       request failed rather than "a quiet day" — and a chart of nothing is
       honest about that. */
    select: (data): HourPoint[] =>
      (data.hours ?? []).map((point) => ({
        hour: point.hour,
        scans: point.scans,
      })),
  })
}

export function dailyScansQuery(
  organizationId: string,
  constellationId: string,
  days = 14,
  timeZone = browserTimeZone(),
) {
  return queryOptions({
    queryKey: keys.analyticsDaily(
      organizationId,
      constellationId,
      timeZone,
      days,
    ),
    queryFn: ({ signal }) =>
      apiFetch<GetDailyScansResult>(
        `/constellations/${constellationId}/analytics/daily?tz=${encodeURIComponent(timeZone)}&days=${days}`,
        { signal },
      ),
    staleTime: ANALYTICS_STALE_TIME,
    /* The sparkline wants bare numbers; the dates are only there to guarantee
       ordering and gap-filling, which the server has already done. */
    select: (data): number[] => (data.days ?? []).map((point) => point.scans),
  })
}

export function scanPlacesQuery(
  organizationId: string,
  constellationId: string,
  days = 30,
  limit = 5,
) {
  return queryOptions({
    queryKey: keys.analyticsLocations(
      organizationId,
      constellationId,
      days,
      limit,
    ),
    queryFn: ({ signal }) =>
      apiFetch<GetScanPlacesResult>(
        `/constellations/${constellationId}/analytics/locations?days=${days}&limit=${limit}`,
        { signal },
      ),
    staleTime: ANALYTICS_STALE_TIME,
    select: (data): Place[] =>
      (data.places ?? []).map((place) => ({
        city: place.city,
        region: place.region,
        scans: place.scans,
      })),
  })
}

/** Per-code totals for the whole organization — what the grid cards read. */
export type ScanTotals = {
  last7Days: number
  last30Days: number
  lifetime: number
  lastScanAt: string | null
}

/**
 * One request for every code's totals.
 *
 * Keyed by organization rather than by code so the grid, the sidebar and the
 * archive all share it. Fetching per card would be an N+1 across the workspace —
 * 500 requests to paint one screen.
 */
export function orgScanTotalsQuery(organizationId: string) {
  return queryOptions({
    queryKey: keys.orgScanTotals(organizationId),
    queryFn: ({ signal }) =>
      apiFetch<GetOrganizationScanTotalsResult>(
        `/organizations/${organizationId}/analytics/scan-totals`,
        { signal },
      ),
    staleTime: ANALYTICS_STALE_TIME,
    select: (data): Record<string, ScanTotals> => {
      const byCode: Record<string, ScanTotals> = {}

      for (const row of data.totals ?? []) {
        byCode[row.constellationId] = {
          last7Days: row.last7Days,
          last30Days: row.last30Days,
          lifetime: row.lifetime,
          lastScanAt: row.lastScanAt,
        }
      }

      return byCode
    },
  })
}
